/**
 * ResizeController — the corner-resize-handle machinery (issue #388)
 * extracted from ConstructionTool (#838, epic #828). A tool OWNS a
 * controller instance and DRIVES it by composition (it is NOT a base
 * class), mirroring the `RotationController` / `OffsetController`
 * pattern: the controller owns the resize drag STATE (`resizeDrag`)
 * plus the hit-testing / preview / commit state machines, while
 * everything construction-specific it needs from the owning tool
 * (scope / scene / project / settings / commit / selection lookups,
 * the shared canvas-preview slot, and the properties-panel live
 * readout) is injected through the `ResizeHost` interface.
 *
 * The resize MATH lives in `src/model/constructionResize.ts`
 * (`resizeBox` / `resizeFlip` / `boxToBoxTransformWithFlip` /
 * `boxToPill` / `pillOrientation`) and is left there; this controller
 * is the interaction layer that reads pointer input, previews the
 * resized shape live, and commits through the model ops.
 *
 * ConstructionTool's `onDown` seeds a drag through `maybeStartDrag`
 * (interleaved with the rotation `maybeStart*` calls — resize runs
 * AFTER the pivot drag but BEFORE the rotation gestures); its
 * `onMove` / `onUp` / `onCancel` delegate the resize case, preserving
 * the exact original dispatch order. The `resizeHandleAt` /
 * `resizeCursorFor` / `resizableSelectedLineId` / `isResizeDragActive`
 * lookups stay public so RotationController's `RotationHost` hooks can
 * delegate to them (rotation and resize share the bbox corners).
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { GuideRay, Scene } from "../canvas/Scene";
import type { ToolSettings } from "./EditorContext";
import {
  projectConstructionLines,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import {
  isCenterSnapActive,
  isPointConstraintActive,
  isSizesSnapActive,
} from "./dragSnap";
import {
  chainReachableLines,
  hasCoincidentConstraint,
  type SnapRefLite,
} from "../model/constraintEnforcement";
import { snapRefToConstraintRef } from "./constructionSnapRefs";
import { newId } from "../model/ids";
import {
  getUnrotatedBounds,
  worldToLineLocal,
} from "../model/constructionRotation";
import {
  allowsEdgeResize,
  boxToBoxTransformWithFlip,
  boxToPill,
  edgeMidpoints,
  isResizeEdge,
  pillOrientation,
  resizePathDataByBox,
  type Box,
  type ResizeEdge,
  type ResizeHandle,
} from "../model/constructionResize";
import {
  applyResizeStep,
  pickResizeHandle,
  resizeCursorFor as sharedResizeCursorFor,
  mapPointThroughResize,
  resizeDragPivot,
  rotatePointAbout,
  unrotatePoint,
} from "../model/transformHandles";
import { rebuildPrimitivePath } from "../model/constructionLineNormalize";
import { setConstructionGeometry } from "../model/constructionRounding";
import { withSnappedPartnersFollowing } from "../model/constructionLineNormalize";
import { setCanvasCursor } from "../canvas/canvasCursor";
import { cssVar } from "../colors";
import { isMirrorDerived } from "../model/constructionMirror";
import { hitRadiusFor, type PointerKind } from "../canvas/pointerPrecision";

/** Selection accent (blue) — matches the static selection overlay drawn by
 *  Scene (`--canvas-accent`). Used for the live resize frame + corner handles
 *  so the blue selection overlay tracks the cursor during a resize. */
const SELECTION_STROKE = () => cssVar("--canvas-accent");
const CONSTRUCTION_STROKE = () => cssVar("--construction-stroke");

/** Everything the ResizeController needs from its owning tool. The
 *  resize drag-state field lives in the controller; every other
 *  collaborator (scope / scene / settings / project / commit, the
 *  shared selection lookup + sub-mode guard, the shared canvas-preview
 *  slot, and the properties-panel live readout) is injected here. */
export interface ResizeHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  getSettings(): ToolSettings;
  commit(label: string, mutate: (proj: Project) => Project): void;
  rerender(): void;
  /** True only while the tool is in the "select" sub-mode — corner
   *  resize handles are a select-mode-only affordance. */
  isSelectSubMode(): boolean;
  /** Id of the sole selected non-offset construction line, or null.
   *  Shared with RotationController (kept in the tool so both
   *  controllers read the same selection state). */
  rotatableSelectedLineId(): string | null;
  /** Remove the tool's shared canvas preview item (draw / resize share
   *  the same slot so any of them can clear a stale preview). */
  cleanupPreview(): void;
  /** Store `item` into the tool's shared canvas preview slot so a later
   *  `cleanupPreview` tears it down. */
  setPreviewItem(item: paper.Item | null): void;
  /** Push the in-progress (uncommitted) resized line to the properties
   *  panel so its dimension readouts track the drag; called with null
   *  on drag end. */
  emitLiveResize(line: ConstructionLine | null): void;
  /** Snap the dragged handle against every enabled snap (#1169), skipping
   *  `excluded` lines, and show the matching indicator. Null when nothing
   *  is in range. */
  snapHandleDrag(
    p: { x: number; y: number },
    excluded: ReadonlySet<string>,
  ): {
    point: { x: number; y: number };
    candidate: SnapRefLite | null;
  } | null;
  /** Remove the indicator `snapHandleDrag` shows. */
  clearHandleDragSnap(): void;
}

/** The rotation frame a resize drag captured at press, or null untilted. */
function pressRotation(drag: {
  rotation: number;
  center: { x: number; y: number };
}): { deg: number; center: { x: number; y: number } } | null {
  return Math.abs(drag.rotation) < 1e-6
    ? null
    : { deg: drag.rotation, center: drag.center };
}

export class ResizeController {
  /** Hit radius (screen px at the live zoom — `value / zoom`) for the corner +
   *  edge resize handles. Generous — roughly double the handle's visual
   *  half-size — so the small squares are easy to grab; the `nearest` pick
   *  resolves any overlap with a neighbouring handle to the closest one. */
  private static readonly RESIZE_HANDLE_HIT_RADIUS_MM = 7;
  /** What is pointing right now (#40) — a finger grabs wider. */
  private pointerKind: PointerKind = "mouse";

  /** Below this a dimension ray is degenerate (a 1-D line's height) — skip it
   *  rather than draw a zero-length stub (#922). */
  private static readonly DIM_MIN_MM = 0.5;

  /** Corner-resize drag (issue #388) — replaces the corner rotation
   *  gesture on the selected line. `origBox` is the line's un-rotated
   *  bounds captured at press; per frame the grabbed `corner` follows
   *  the cursor (opposite corner pinned, or centre pinned under Alt /
   *  centre snap) and a dashed preview shows the new outline. */
  private resizeDrag: {
    lineId: string;
    handle: ResizeHandle;
    origBox: Box;
    /** The line's baked rotation (deg) + pivot at press. Resize runs in
     *  the un-rotated frame and re-applies these so the tilt is kept. */
    rotation: number;
    center: { x: number; y: number };
    /** Snapped partners whose static render is hidden because the preview
     *  is drawing them at their followed position (#1166). Kept for the
     *  rest of the drag, so a partner that drifts back to its original spot
     *  is still drawn rather than vanishing. */
    partnerIds: Set<string>;
    /** Lines the handle must not snap to: the resized line and everything
     *  linked to it, captured at press. They move with the drag, so their
     *  model geometry is stale mid-gesture (#1169). */
    snapExcluded: Set<string>;
    /** What the handle snapped to on the latest frame, or null. */
    snap: {
      point: { x: number; y: number };
      candidate: SnapRefLite | null;
    } | null;
  } | null = null;

  constructor(private host: ResizeHost) {}

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** True while a resize drag is in flight. Exposed so RotationController's
   *  `RotationHost.isResizeDragActive` hook can delegate here (rotation
   *  hovers yield to an active resize). */
  isResizeDragActive(): boolean {
    return !!this.resizeDrag;
  }

  /** onMove delegation: update the live resize preview. Returns true when
   *  a resize drag consumed the move. */
  handleMove(p: { x: number; y: number }, s: PointerSample): boolean {
    if (!this.resizeDrag) return false;
    this.updateResizeDrag(p, s);
    return true;
  }

  /** onUp delegation: commit the resize and clear the drag. Returns true
   *  when a resize drag consumed the release. */
  handleUp(p: { x: number; y: number }, s: PointerSample): boolean {
    if (!this.resizeDrag) return false;
    this.commitResizeDrag(p, s);
    return true;
  }

  /** onCancel delegation: drop an in-flight resize drag and restore the
   *  static overlay via a re-render. */
  handleCancel(): void {
    if (!this.resizeDrag) return;
    this.resizeDrag = null;
    this.host.clearHandleDragSnap();
    this.host.cleanupPreview();
    this.host.getScene().setTransientPivot(null);
    this.host.getScene().setGuideRays(null);
    this.host.emitLiveResize(null);
    this.host.rerender();
  }

  // -------------------------------------------------------------------
  // Selection / hit-testing (shared with RotationController's host hooks)
  // -------------------------------------------------------------------

  /** The single selected line that should show corner RESIZE handles
   *  (issue #388), or null. Requirements: select sub-mode, a single
   *  non-offset, non-mirror line, and a 2-D bounding box (degenerate 1-D lines keep
   *  their rotation affordance). Resize runs in the line's UN-rotated
   *  frame, so the line's `rotation` / `rotationCenter` are returned
   *  alongside its un-rotated bounds — a rotated line resizes too, with
   *  the tilt preserved (the opposite displayed corner stays pinned). */
  private resizableSelectedLine(): {
    line: ConstructionLine;
    box: Box;
    rotation: number;
    center: { x: number; y: number };
  } | null {
    if (!this.host.isSelectSubMode()) return null;
    const id = this.host.rotatableSelectedLineId();
    if (!id) return null;
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === id,
    );
    if (!line) return null;
    // A mirror re-derives from its source on commit, so a resize would undo
    // itself on release (#1167). Its rotation knob stays — that gesture
    // routes to the source.
    if (isMirrorDerived(line)) return null;
    const scope = this.host.getScope();
    const b = getUnrotatedBounds(line, scope);
    if (!b || b.width <= 1e-3 || b.height <= 1e-3) return null;
    return {
      line,
      box: { x: b.x, y: b.y, width: b.width, height: b.height },
      rotation: line.rotation ?? 0,
      center: { x: b.cx, y: b.cy },
    };
  }

  /** RotationHost delegate: id of the single selected non-offset line
   *  currently showing corner RESIZE handles (#388), or null. */
  resizableSelectedLineId(): string | null {
    return this.resizableSelectedLine()?.line.id ?? null;
  }

  /** Which resize handle of the selected resizable line `p` falls on, or
   *  null. Corners (2-D scale) take priority over the edge midpoints
   *  (single-dimension, #863) when both are in range. The cursor is mapped
   *  into the line's UN-rotated frame (`worldToLineLocal`) before the
   *  axis-aligned check, so the handles track the tilted bbox of a rotated
   *  line. Edge handles are offered only for shapes where a one-axis scale is
   *  sensible (`allowsEdgeResize`). */
  resizeHandleAt(p: { x: number; y: number }): {
    lineId: string;
    handle: ResizeHandle;
    box: Box;
    rotation: number;
    center: { x: number; y: number };
  } | null {
    const sel = this.resizableSelectedLine();
    if (!sel) return null;
    const { box } = sel;
    const local = worldToLineLocal(sel.line, p);
    // Screen-constant hit target (#864): the handle square is drawn at a
    // fixed on-screen size (`LINE_RESIZE_HANDLE_HALF_MM / zoom`), so the
    // grab radius divides by the live zoom too — the clickable zone tracks
    // the visible square instead of ballooning when zoomed in. The pick
    // itself is the shared one leather's transform frame uses (#990).
    const handle = pickResizeHandle(box, local, {
      zoom: this.host.getScope().view?.zoom ?? 1,
      radiusMm: hitRadiusFor(
        ResizeController.RESIZE_HANDLE_HIT_RADIUS_MM,
        this.pointerKind,
      ),
      allowEdgeResize: allowsEdgeResize(sel.line.primitive?.kind),
    });
    if (!handle) return null;
    return {
      lineId: sel.line.id,
      handle,
      box,
      rotation: sel.rotation,
      center: sel.center,
    };
  }

  /** Resize cursor for a handle — the shared mapping (#990), exposed here
   *  because `RotationHost.resizeCursorFor` delegates to this controller. */
  resizeCursorFor(handle: ResizeHandle): string {
    return sharedResizeCursorFor(handle);
  }

  // -------------------------------------------------------------------
  // Drag lifecycle
  // -------------------------------------------------------------------

  /** Start a corner-resize drag when the press lands on a resize
   *  handle of the selected line. Returns true when it claimed the
   *  gesture (so onDown skips rotation / translation). */
  /** Tell the controller what is pointing, before the hit tests run (#40). */
  setPointerKind(kind: PointerKind) {
    this.pointerKind = kind;
  }

  maybeStartDrag(p: { x: number; y: number }): boolean {
    const hit = this.resizeHandleAt(p);
    if (!hit) return false;
    this.resizeDrag = {
      lineId: hit.lineId,
      handle: hit.handle,
      origBox: hit.box,
      rotation: hit.rotation,
      center: hit.center,
      partnerIds: new Set(),
      snapExcluded: chainReachableLines(this.host.getProject(), [hit.lineId]),
      snap: null,
    };
    // Hide the static line + its selection overlay so the live preview drawn
    // each frame in `updateResizeDrag` is the only thing on screen — the
    // resize then reads as the real shape moving, not a ghost over a frozen
    // original. Every drag-end path (commit / no-op / cancel) re-renders,
    // which restores the hidden items.
    this.host.getScene().beginConstructionResizePreview(hit.lineId);
    return true;
  }

  /** Compute the resized bounding box for the in-flight drag, honouring
   *  size snap (aspect lock — Shift or the sizes toggle), centre snap
   *  (resize about the centre — Alt or the centre toggle) and Grid snap
   *  (the dragged corner lands on the lattice). The cursor is first mapped
   *  into the line's un-rotated frame so a rotated line resizes about its
   *  tilted corners.
   *
   *  Two shape-specific constraints keep the resize sensible:
   *   - A single-segment **line** has no area, so resize is a pure length
   *     change: force uniform scaling (an independent x/y scale would shear
   *     its slope).
   *   - A **pill** resizes its length only unless proportional scaling is
   *     held — pin the short-axis dimension so the cap radius / width stays
   *     put while the long axis grows. */
  private resizeBoxForDrag(
    drag: NonNullable<typeof this.resizeDrag>,
    p: { x: number; y: number },
    s: PointerSample,
  ): { box: Box; flip: { x: boolean; y: boolean } } {
    // `p` arrives already snapped (`snapHandle`), in world space, so a
    // rotated line's corner lands on the snapped point too. Size / center
    // snaps still refine the box below and win for that frame.
    const local = this.rotatePointInverse(p, drag.center, drag.rotation);
    const settings = this.host.getSettings();
    const prim = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === drag.lineId,
    )?.primitive;
    const proportional = isSizesSnapActive(settings, s.shiftKey);
    const fromCenter = isCenterSnapActive(settings, s.altKey);
    // Edge (single-dimension) resize (#863): only the grabbed edge's axis
    // changes; the other keeps `origBox`. Edge handles are offered only for
    // rect / ellipse / generic shapes, so no line / pill special-casing here.
    if (isResizeEdge(drag.handle)) {
      return applyResizeStep(drag.origBox, drag.handle, local, { fromCenter });
    }
    const pillOrient =
      prim?.kind === "pill" ? pillOrientation(prim) : undefined;
    // A DIAGONAL pill has no axis-aligned box mapping, so an independent x/y
    // scale would skew the capsule — force uniform scaling (like a line) so it
    // keeps its shape and just grows / shrinks along the diagonal.
    const diagonalPill = prim?.kind === "pill" && pillOrient === null;
    // `flip` tracks the dragged corner crossing its pinned reference so the
    // geometry mirrors across the boundary (asymmetric shapes flip; the box
    // itself is unchanged by the reflection).
    const { box, flip } = applyResizeStep(drag.origBox, drag.handle, local, {
      aspectLock: prim?.kind === "line" || diagonalPill || proportional,
      fromCenter,
    });
    // Axis-aligned pill, length-only (no proportional modifier): pin the
    // short-axis dimension so only the long axis grows.
    if (prim?.kind === "pill" && !proportional) {
      if (pillOrient === "horizontal") {
        return {
          box: { ...box, y: drag.origBox.y, height: drag.origBox.height },
          flip,
        };
      }
      if (pillOrient === "vertical") {
        return {
          box: { ...box, x: drag.origBox.x, width: drag.origBox.width },
          flip,
        };
      }
    }
    return { box, flip };
  }

  /** Rotate `p` by `-deg` about `c` — maps a world point into the
   *  un-rotated frame of an entity tilted by `deg`. (A standalone twin of
   *  `worldToLineLocal` that takes an explicit pivot/angle; both now sit on
   *  the shared `unrotatePoint`.) */
  private rotatePointInverse(
    p: { x: number; y: number },
    c: { x: number; y: number },
    deg: number,
  ): { x: number; y: number } {
    return unrotatePoint(p, c, deg);
  }

  /** Build the resized pathData (and, for rect / ellipse, an updated
   *  primitive) for a target box. All scaling happens in the line's
   *  UN-rotated frame, then the baked tilt (`rotation` about `center`) is
   *  re-applied so a rotated line keeps its angle. Rect / ellipse stay
   *  parametric via `rebuildPrimitivePath` (their metadata is the
   *  un-rotated box); every other kind un-rotates its pathData, scales it
   *  by the box-to-box affine, re-rotates, and drops any primitive (a
   *  non-uniform scale can't stay a pill / line). */
  private resizedGeometry(
    line: ConstructionLine,
    origBox: Box,
    nextBox: Box,
    rotation: number,
    center: { x: number; y: number },
    flip: { x: boolean; y: boolean } = { x: false, y: false },
  ): { pathData: string; primitive: ConstructionLine["primitive"] | null } {
    const scope = this.host.getScope();
    const rotated = Math.abs(rotation) > 1e-6;
    const pivot = new scope.Point(center.x, center.y);
    const prim = line.primitive;
    if (prim && (prim.kind === "rectangle" || prim.kind === "ellipse")) {
      const next = {
        ...prim,
        x: nextBox.x,
        y: nextBox.y,
        width: nextBox.width,
        height: nextBox.height,
      };
      const path = new scope.CompoundPath({
        pathData: rebuildPrimitivePath(next, scope),
        insert: false,
      });
      if (rotated) path.rotate(rotation, pivot);
      const data = path.pathData;
      path.remove();
      return { pathData: data, primitive: next };
    }
    // Line / pill endpoints are stored in WORLD coords (a rotation bakes the
    // tilt into them, keeping `rotation` only for the bbox-tilt invariant —
    // see `rotateConstructionLine`). So resize them in the un-rotated frame:
    // un-rotate the endpoints about the pivot, apply the un-rotated box affine,
    // then rotate the result back. `rebuildPrimitivePath` then draws the
    // already-world endpoints directly — no extra `path.rotate` (that was the
    // source of the double-rotation bug).
    const unrotate = (pt: { x: number; y: number }) =>
      this.rotatePointInverse(pt, center, rotation);
    const rerotate = (pt: { x: number; y: number }) =>
      this.rotatePointInverse(pt, center, -rotation);
    if (prim && prim.kind === "line") {
      const t = boxToBoxTransformWithFlip(origBox, nextBox, flip);
      const u1 = unrotate({ x: prim.x1, y: prim.y1 });
      const u2 = unrotate({ x: prim.x2, y: prim.y2 });
      const n1 = rerotate({ x: t.sx * u1.x + t.tx, y: t.sy * u1.y + t.ty });
      const n2 = rerotate({ x: t.sx * u2.x + t.tx, y: t.sy * u2.y + t.ty });
      const next = { ...prim, x1: n1.x, y1: n1.y, x2: n2.x, y2: n2.y };
      const path = new scope.CompoundPath({
        pathData: rebuildPrimitivePath(next, scope),
        insert: false,
      });
      const data = path.pathData;
      path.remove();
      return { pathData: data, primitive: next };
    }
    if (prim && prim.kind === "pill") {
      const u1 = unrotate({ x: prim.x1, y: prim.y1 });
      const u2 = unrotate({ x: prim.x2, y: prim.y2 });
      // Orientation is judged in the UN-rotated frame, so a rotated capsule
      // that was drawn axis-aligned still resizes length-only.
      const orientation = pillOrientation({
        x1: u1.x,
        y1: u1.y,
        x2: u2.x,
        y2: u2.y,
      });
      // Axis-aligned → rebuild from the target box (length / width). Diagonal →
      // scale UNIFORMLY (the drag forced aspect-lock) so the capsule keeps its
      // shape un-skewed: endpoints through the affine, width by the same factor.
      let nu: { x1: number; y1: number; x2: number; y2: number; width: number };
      if (orientation) {
        nu = boxToPill(nextBox, orientation);
      } else {
        const t = boxToBoxTransformWithFlip(origBox, nextBox, flip);
        nu = {
          x1: t.sx * u1.x + t.tx,
          y1: t.sy * u1.y + t.ty,
          x2: t.sx * u2.x + t.tx,
          y2: t.sy * u2.y + t.ty,
          width: prim.width * Math.sqrt(Math.abs(t.sx * t.sy)),
        };
      }
      const w1 = rerotate({ x: nu.x1, y: nu.y1 });
      const w2 = rerotate({ x: nu.x2, y: nu.y2 });
      const next = {
        ...prim,
        x1: w1.x,
        y1: w1.y,
        x2: w2.x,
        y2: w2.y,
        width: nu.width,
      };
      const path = new scope.CompoundPath({
        pathData: rebuildPrimitivePath(next, scope),
        insert: false,
      });
      const data = path.pathData;
      path.remove();
      return { pathData: data, primitive: next };
    }
    // Work in the un-rotated frame: undo the tilt, scale, then re-apply (a
    // flipped axis carries a negative scale so the geometry mirrors). Shared
    // with the leather transform controller via `resizePathDataByBox` (#971) —
    // leather passes no rotation, construction passes its baked tilt.
    // A rounded line scales its SHARP source; `resizedLine` re-applies the
    // rounding, so the corner radii stay the same instead of the arcs
    // stretching with the box (#1165).
    const data = resizePathDataByBox(
      line.cornerRounding?.sharpPathData ?? line.pathData,
      origBox,
      nextBox,
      flip,
      scope,
      rotated ? { deg: rotation, pivot: center } : undefined,
    );
    return { pathData: data, primitive: null };
  }

  /** The cursor with every enabled snap applied (#1169) — another line's
   *  anchor / midpoint / center (Point), a crossing or a point along a line
   *  (Line), or the mat grid — skipping the resized line and everything that
   *  follows it. The dragged handle lands exactly there; an edge handle takes
   *  only the coordinate on its own axis, so the edge lines up with the snap.
   *  Records the hit on `drag.snap` for the release. */
  private snapHandle(
    drag: NonNullable<ResizeController["resizeDrag"]>,
    p: { x: number; y: number },
  ): { x: number; y: number } {
    const excluded = new Set([...drag.snapExcluded, ...drag.partnerIds]);
    drag.snap = this.host.snapHandleDrag(p, excluded);
    return drag.snap?.point ?? p;
  }

  /** With Point constraint on, a corner that snapped onto another line's
   *  anchor (or midpoint / center) is linked to it, the way a dragged vertex
   *  is. Only when one of the resized line's own anchors actually sits on the
   *  snapped point: a rounded corner or an ellipse's bbox corner has no
   *  anchor there, and an edge handle aligns just one coordinate, so those
   *  snap by position alone. */
  private withHandleSnapLink(
    proj: Project,
    id: string,
    snap: NonNullable<ResizeController["resizeDrag"]>["snap"],
  ): Project {
    if (!snap?.candidate) return proj;
    if (!isPointConstraintActive(this.host.getSettings())) return proj;
    const line = projectConstructionLines(proj).find((l) => l.id === id);
    if (!line) return proj;
    const scope = this.host.getScope();
    const cp = new scope.CompoundPath({
      pathData: line.pathData,
      insert: false,
    });
    const subs = (cp.children.length > 0 ? cp.children : [cp]) as paper.Path[];
    let source: SnapRefLite | null = null;
    subs.forEach((sub, pathIndex) => {
      sub.segments.forEach((seg, segmentIndex) => {
        if (source) return;
        if (
          Math.abs(seg.point.x - snap.point.x) < 1e-6 &&
          Math.abs(seg.point.y - snap.point.y) < 1e-6
        ) {
          source = {
            kind: "anchor",
            constructionId: id,
            pathIndex,
            segmentIndex,
          };
        }
      });
    });
    if (!source) return proj;
    const src: SnapRefLite = source;
    if (hasCoincidentConstraint(proj, src, snap.candidate)) return proj;
    return {
      ...proj,
      constraints: [
        ...(proj.constraints ?? []),
        {
          id: newId(),
          kind: "coincident",
          refs: [
            snapRefToConstraintRef(src),
            snapRefToConstraintRef(snap.candidate),
          ],
        },
      ],
    };
  }

  /** `lines` (already holding the resized line `id`) with every snapped
   *  partner following it, as the project to commit.
   *
   *  Partners follow from EVERY anchor of the resized line plus the center
   *  and edge midpoints of a primitive — the commit used to seed only the
   *  first two anchors, leaving a line linked to a rectangle's third corner
   *  behind. A partner linked at one point moves; one linked at two points
   *  stretches so both ends stay attached. The resized shape itself is
   *  final: it is what the cursor produced, and enforcement never pulls its
   *  anchors back (#1166). The live preview runs the same thing. */
  private withSnappedPartners(
    proj: Project,
    lines: ConstructionLine[],
    id: string,
  ): Project {
    return withSnappedPartnersFollowing(
      proj,
      lines,
      [id],
      this.host.getScope(),
    );
  }

  /** Partners that move when `line` becomes `resized`, at their followed
   *  geometry — every line the commit's propagation changes, plus any partner
   *  already hidden earlier in this drag. Hides each one's static render. */
  private followingPartners(
    line: ConstructionLine,
    resized: ConstructionLine,
    drag: NonNullable<ResizeController["resizeDrag"]>,
  ): ConstructionLine[] {
    const proj = this.host.getProject();
    const before = projectConstructionLines(proj);
    const lines = before.map((l) => (l.id === line.id ? resized : l));
    const after = projectConstructionLines(
      this.withSnappedPartners(proj, lines, line.id),
    );
    const beforeById = new Map(before.map((l) => [l.id, l]));
    const scene = this.host.getScene();
    const out: ConstructionLine[] = [];
    for (const l of after) {
      if (l.id === line.id) continue;
      const moved = l.pathData !== beforeById.get(l.id)?.pathData;
      if (!moved && !drag.partnerIds.has(l.id)) continue;
      if (!drag.partnerIds.has(l.id)) {
        drag.partnerIds.add(l.id);
        scene.beginConstructionResizePreview(l.id);
      }
      out.push(l);
    }
    return out;
  }

  /** The selected line as it looks after the resize: `resizedGeometry`'s
   *  sharp outline written through `setConstructionGeometry`, so a rounded
   *  line gets its `cornerRounding.sharpPathData` updated and its corners
   *  re-rounded. Writing only `pathData` + `primitive` left the sharp source
   *  stale, and the commit's rounding pass redrew the old shape while the
   *  primitive kept the new size (#1165). The live preview and the commit
   *  both use this, so what the drag shows is what lands. */
  private resizedLine(
    line: ConstructionLine,
    origBox: Box,
    nextBox: Box,
    rotation: number,
    center: { x: number; y: number },
    flip: { x: boolean; y: boolean },
  ): ConstructionLine {
    const { pathData, primitive } = this.resizedGeometry(
      line,
      origBox,
      nextBox,
      rotation,
      center,
      flip,
    );
    return setConstructionGeometry(
      line,
      pathData,
      primitive ?? undefined,
      this.host.getScope(),
    );
  }

  /** Per-frame resize preview. The static line + its selection overlay are
   *  hidden for the duration of the drag (see `maybeStartDrag`), and
   *  this draws a LIVE stand-in that tracks the cursor: the resized shape plus
   *  the bbox frame and the four corner handle squares at the new box (tilted
   *  to match a rotated line). The committed geometry only changes on release,
   *  but the user now sees the whole overlay move instead of a frozen one. */
  private updateResizeDrag(
    p: { x: number; y: number },
    s: PointerSample,
  ): void {
    const drag = this.resizeDrag;
    if (!drag) return;
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === drag.lineId,
    );
    if (!line) return;
    const { box: nextBox, flip } = this.resizeBoxForDrag(
      drag,
      this.snapHandle(drag, p),
      s,
    );
    const resized = this.resizedLine(
      line,
      drag.origBox,
      nextBox,
      drag.rotation,
      drag.center,
      flip,
    );
    const { pathData } = resized;
    // Mirror the in-progress geometry into the properties panel so its
    // dimension readouts track the drag (issue #388).
    this.host.emitLiveResize(resized);
    const scope = this.host.getScope();
    this.host.cleanupPreview();

    // The resized shape itself (rotation already baked into `pathData`). Drawn
    // in the selection blue so the live stand-in matches the selected
    // construction line it replaces, rather than the yellow draw-preview.
    const shape = new scope.CompoundPath({ pathData });
    shape.strokeColor = new scope.Color(SELECTION_STROKE());
    shape.strokeWidth = 1.6;
    shape.strokeScaling = false;
    shape.dashArray = [2, 1.5];
    shape.fillColor = null;

    // Live selection overlay: bbox frame + corner handle squares at the new
    // box, built in the un-rotated frame and tilted to match the line.
    const frame = new scope.Path.Rectangle({
      rectangle: new scope.Rectangle(
        nextBox.x,
        nextBox.y,
        nextBox.width,
        nextBox.height,
      ),
      strokeColor: new scope.Color(SELECTION_STROKE()),
      fillColor: null,
    });
    frame.strokeWidth = 1;
    frame.strokeScaling = false;
    frame.dashArray = [2, 2];
    const overlay = new scope.Group([frame]);
    // Screen-constant handle squares (#864): matches Scene.LINE_RESIZE_HANDLE_HALF_MM
    // divided by the live zoom, so the live-drag preview handles stay the same
    // on-screen size as the static overlay squares.
    const half = 1.4 / (scope.view?.zoom ?? 1);
    const handlePts: Array<readonly [number, number]> = [
      [nextBox.x, nextBox.y],
      [nextBox.x + nextBox.width, nextBox.y],
      [nextBox.x, nextBox.y + nextBox.height],
      [nextBox.x + nextBox.width, nextBox.y + nextBox.height],
    ];
    // Edge (single-dimension) handle squares (#863) — at the new box's edge
    // midpoints, for the shapes that offer them.
    if (allowsEdgeResize(line.primitive?.kind)) {
      const mids = edgeMidpoints(nextBox);
      for (const e of ["t", "r", "b", "l"] as const) {
        handlePts.push([mids[e].x, mids[e].y]);
      }
    }
    for (const [hx, hy] of handlePts) {
      const sq = new scope.Path.Rectangle({
        rectangle: new scope.Rectangle(
          hx - half,
          hy - half,
          half * 2,
          half * 2,
        ),
        fillColor: new scope.Color(SELECTION_STROKE()),
        strokeColor: null,
      });
      sq.strokeScaling = false;
      overlay.addChild(sq);
    }
    if (Math.abs(drag.rotation) > 1e-6) {
      overlay.rotate(
        drag.rotation,
        new scope.Point(drag.center.x, drag.center.y),
      );
    }

    const group = new scope.Group([shape, overlay]);
    // Snapped partners follow live, the same way the commit will move them
    // (#1166): the static partner hides and a stand-in draws where it goes.
    // The hidden items (the resized line's and each partner's) carry the
    // in-flight geometry too, so the snap crosshair markers — which read
    // live rendered positions — travel with the drag.
    const scene = this.host.getScene();
    const partners = this.followingPartners(line, resized, drag);
    scene.setConstructionResizePreviewPath(line.id, resized.pathData);
    for (const partner of partners) {
      scene.setConstructionResizePreviewPath(partner.id, partner.pathData);
    }
    scene.refreshConstructionCrosshairsLive();
    for (const partner of partners) {
      const path = new scope.CompoundPath({ pathData: partner.pathData });
      path.strokeColor = new scope.Color(CONSTRUCTION_STROKE());
      path.strokeWidth = 1.2;
      path.strokeScaling = false;
      path.fillColor = null;
      group.insertChild(0, path);
    }
    group.data = { ephemeral: true };
    this.host.setPreviewItem(group);
    setCanvasCursor(scope, this.resizeCursorFor(drag.handle));

    // Track the rotation pivot live during the resize (issue #420): the
    // default pivot is the displayed bbox centre, which moves as the box grows
    // / shrinks, and a custom `pivotPoint` travels with the line (#1111). The
    // rule is shared with leather's resize. Drive it through the
    // transient-pivot crosshair so it follows the preview instead of freezing
    // at the pre-resize position until commit.
    const livePivot = resizeDragPivot(
      drag.origBox,
      nextBox,
      flip,
      pressRotation(drag),
      line.pivotPoint ? { x: line.pivotPoint.x, y: line.pivotPoint.y } : null,
    );
    this.host.getScene().setTransientPivot(livePivot);

    // On-canvas width / height dimension rays (#922) — the panel readouts alone
    // make the maker look away from the shape. Additive to the panel mirror above.
    this.showResizeDims(nextBox, drag.rotation, drag.center);
  }

  /** Width + height dimension rays for the live resize (#922). Built in the
   *  line's un-rotated frame then rotated to match, so a tilted line's dims run
   *  along its edges. Skips a near-zero dimension (e.g. a 1-D line's height).
   *  Uses the shared guide-ray system, so it respects the rays toggle + the
   *  leather accent for free. */
  private showResizeDims(
    box: Box,
    rotation: number,
    center: { x: number; y: number },
  ): void {
    const tl = this.rotatePointForward(
      { x: box.x, y: box.y },
      center,
      rotation,
    );
    const tr = this.rotatePointForward(
      { x: box.x + box.width, y: box.y },
      center,
      rotation,
    );
    const bl = this.rotatePointForward(
      { x: box.x, y: box.y + box.height },
      center,
      rotation,
    );
    const rays: GuideRay[] = [];
    if (box.width > ResizeController.DIM_MIN_MM) {
      rays.push({ from: tl, to: tr, lengthMm: box.width });
    }
    if (box.height > ResizeController.DIM_MIN_MM) {
      rays.push({ from: tl, to: bl, lengthMm: box.height });
    }
    this.host.getScene().setGuideRays(rays.length ? rays : null);
  }

  /** Rotate `p` by `+deg` about `c` — the forward twin of
   *  `rotatePointInverse`, mapping an un-rotated-frame point back into world. */
  private rotatePointForward(
    p: { x: number; y: number },
    c: { x: number; y: number },
    deg: number,
  ): { x: number; y: number } {
    return rotatePointAbout(p, c, deg);
  }

  /** Commit the resize: rect / ellipse / line / axis-aligned pill stay
   *  parametric, everything else scales its pathData. A freehand curve also
   *  scales its `rawPoints` source so the commit-time smoothing re-derive
   *  (which rebuilds pathData from those points) reproduces the resized curve
   *  instead of reverting it. Coincident constraints on the two
   *  bbox-defining anchors of a primitive follow, mirroring the
   *  Properties-panel resize. */
  private commitResizeDrag(
    p: { x: number; y: number },
    s: PointerSample,
  ): void {
    const drag = this.resizeDrag;
    this.resizeDrag = null;
    // Snap the release point exactly as the last frame did, then drop the
    // indicator.
    const snappedP = drag ? this.snapHandle(drag, p) : p;
    this.host.clearHandleDragSnap();
    this.host.cleanupPreview();
    // Drop the live resize pivot crosshair — the commit / re-render
    // below redraws the persistent marker at the committed centre.
    this.host.getScene().setTransientPivot(null);
    this.host.getScene().setGuideRays(null);
    // Clear the live panel override — the commit below (or the early-out
    // re-render) repaints the panel from the committed geometry.
    this.host.emitLiveResize(null);
    if (!drag) return;
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === drag.lineId,
    );
    if (!line) {
      this.host.rerender();
      return;
    }
    const { box: nextBox, flip } = this.resizeBoxForDrag(drag, snappedP, s);
    // No meaningful change (a click without a drag) — just repaint so the
    // preview clears and the static overlay comes back. A flip is a real
    // change even at the same box size, so it skips this early-out.
    if (
      !flip.x &&
      !flip.y &&
      Math.abs(nextBox.width - drag.origBox.width) < 1e-3 &&
      Math.abs(nextBox.height - drag.origBox.height) < 1e-3 &&
      Math.abs(nextBox.x - drag.origBox.x) < 1e-3 &&
      Math.abs(nextBox.y - drag.origBox.y) < 1e-3
    ) {
      this.host.rerender();
      return;
    }
    const resized = this.resizedLine(
      line,
      drag.origBox,
      nextBox,
      drag.rotation,
      drag.center,
      flip,
    );
    const scope = this.host.getScope();
    const id = drag.lineId;
    // Affine that maps the original bbox onto the resized one (mirroring on a
    // flipped axis) — used to scale a freehand curve's raw source points. Only
    // valid as a plain affine when the line is unrotated (a tilted resize is
    // unrotate→scale→re-rotate).
    const affine = boxToBoxTransformWithFlip(drag.origBox, nextBox, flip);
    const unrotated = Math.abs(drag.rotation) < 1e-6;
    this.host.commit("Resize construction line", (proj) => {
      const lines = (proj.constructionLines ?? []).map((l) => {
        if (l.id !== id) return l;
        const next: ConstructionLine = {
          ...l,
          pathData: resized.pathData,
          primitive: resized.primitive,
          ...(resized.cornerRounding
            ? { cornerRounding: resized.cornerRounding }
            : {}),
          // Resize runs in the un-rotated frame and re-bakes the tilt
          // into pathData, so the rotation field carries over unchanged
          // (the displayed shape keeps its angle). A parked pivot travels
          // with the geometry, through the same map the live preview used,
          // so it stays where it was shown (#1111).
          ...(l.pivotPoint
            ? {
                pivotPoint: mapPointThroughResize(
                  l.pivotPoint,
                  drag.origBox,
                  nextBox,
                  flip,
                  pressRotation(drag),
                ),
              }
            : {}),
        };
        // Freehand curves re-derive their pathData from `rawPoints` on every
        // commit (deriveFreehandSmoothing), so scale those source points too
        // or the next derive pass reverts the resize. A tilted resize bakes
        // the angle into pathData only, so there we drop the raw source (the
        // curve becomes a plain, non-re-smoothable path).
        if (l.rawPoints) {
          if (unrotated) {
            next.rawPoints = l.rawPoints.map((pt) => ({
              x: affine.sx * pt.x + affine.tx,
              y: affine.sy * pt.y + affine.ty,
            }));
          } else {
            next.rawPoints = undefined;
            next.smoothness = undefined;
          }
        }
        return next;
      });
      const next = this.withSnappedPartners(proj, lines, id);
      return this.withHandleSnapLink(next, id, drag.snap);
    });
  }
}
